import { InState, OutState, PropsAnimatorOptions } from '../../components/PropsAnimator/PropsAnimator';
import { getLayoutKeyForControl } from '../../components/RibbonBottomBar/addLayoutCacheKeyToRibbonState';
import { RibbonControlInternalProps } from '../RibbonControlProps';
import {
  labelEnterTransitionDuration,
  labelExitTransitionDuration,
  labelTransitionClassNames,
} from './RibbonButton.styles';

interface TransitionLabelInState {
  hideLabel?: boolean;
}

interface DataFromHiddenRender {
  finalWidth: number;
}

export type HideLabelAnimationProps = {
  animate?: boolean;
  hideLabel?: boolean;
  textContainerInitialWidth?: number;
  textContainerFinalWidth?: number;
};

export type RibbonControlWithHideableLabel = RibbonControlInternalProps & HideLabelAnimationProps;

const textContainerSelector = '.ms-Button-textContainer';
const transition = {
  className: labelTransitionClassNames,
  inDuration: labelEnterTransitionDuration,
  outDuration: labelExitTransitionDuration,
};

export function getHideLabelAnimationOptions<TProps extends RibbonControlWithHideableLabel>() {
  const options: PropsAnimatorOptions<TProps, HideLabelAnimationProps, TransitionLabelInState, DataFromHiddenRender> = {
    shouldAnimate,
    getInitialInState,
    getDataFromHiddenRender,
    getInState,
    getCacheKeyForHiddenRender,
    getOutState,
    shouldAnimateOut,
    transition,
  };

  return options;
}

function shouldAnimate<TProps extends RibbonControlWithHideableLabel>(nextProps: TProps) {
  return !!nextProps.animate;
}

function getInitialInState<TProps extends RibbonControlWithHideableLabel>(nextProps: TProps) {
  return { state: { hideLabel: nextProps.hideLabel } };
}

/**
 * We'll provide initial and final width values for the text container. To animate smoothly from the old width to the new width
 */
function getInState<TProps extends RibbonControlWithHideableLabel>(
  props: TProps,
  _previousOutState: OutState<TProps>,
  visibleWrapper: HTMLElement,
  valueFromHiddenRender: DataFromHiddenRender
): InState<TransitionLabelInState, HideLabelAnimationProps> {
  const textContainer = visibleWrapper.querySelector(textContainerSelector);
  return {
    animationProps: {
      textContainerInitialWidth: (textContainer && textContainer.clientWidth) || 0,
      textContainerFinalWidth: valueFromHiddenRender.finalWidth || 0,
    } as Partial<TProps>,
    state: {
      hideLabel: props.hideLabel,
    },
  };
}

function shouldAnimateOut<TProps extends RibbonControlWithHideableLabel>(
  nextProps: TProps,
  inState: InState<HideLabelAnimationProps, TransitionLabelInState>
): boolean {
  if (!!inState.state!.hideLabel !== !!nextProps.hideLabel) {
    return true;
  }
  return false;
}

function getCacheKeyForHiddenRender<TProps extends RibbonControlWithHideableLabel>(props: TProps) {
  return getLayoutKeyForControl(props);
}

/** Measure the textContainer for the hidden button so we can use it as the final width for the new props */
function getDataFromHiddenRender(hiddenWrapper: HTMLElement) {
  const textContainer = hiddenWrapper.querySelector(textContainerSelector);
  return {
    finalWidth: (textContainer && textContainer.clientWidth) || 0,
  };
}

/**
 * Get the state needed to Exit the previous props
 */
function getOutState<TProps extends RibbonControlWithHideableLabel>(
  _nextProps: TProps,
  previousInState: InState<HideLabelAnimationProps, TransitionLabelInState>
): OutState<TProps> {
  return {
    offsetExitedProps: {
      hideLabel: previousInState.state!.hideLabel,
    } as Partial<TProps>,
    hiddenPropOverrides: {
      animate: false,
    } as Partial<TProps>,
  };
}
